import { applicationFormFields, emptyJobValues, errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { JobBuilder } from "./-components/job-builder";
/* From its own module: validateSearch is not split out with the component, and
   through job-builder.tsx it put the builder in the router's entry chunk. */
import { jobBuilderSearch } from "./-components/job-builder-search";
import { type JobDraft } from "./-components/job-form";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/jobs/new")({
  validateSearch: jobBuilderSearch,
  component: NewJobPage,
});

/** Writing a job posting: the advert itself, what an applicant is asked, and the paperwork that
 * follows them to their first day. */
function NewJobPage() {
  const navigate = useNavigate();
  const { step } = Route.useSearch();
  const queryClient = useQueryClient();
  const { facilityId } = useFacility();
  const canManage = useCan("hiring:manage");

  const save = useMutation({
    /** The question set, the forms and the documents all hang off a job that does not exist until
     * the first call returns, so they follow it — together, so a posting with three documents costs
     * one round trip rather than three. */
    mutationFn: async (draft: JobDraft) => {
      const job = await orpcClient.workforce.hiring.createJob({
        ...draft.values,
        screeningQuestions: draft.screeningQuestions,
        ...applicationFormFields(draft),
      });
      await Promise.all([
        orpcClient.workforce.hiringCatalog.setJobFormTemplates({
          jobId: job.id,
          formTemplateIds: draft.formTemplateIds,
        }),
        orpcClient.workforce.hiringCatalog.setJobDocuments({
          jobId: job.id,
          assignments: draft.documents,
        }),
        draft.screeningTemplateId
          ? orpcClient.workforce.hiringTemplates.screening.setForJob({
              jobId: job.id,
              screeningTemplateId: draft.screeningTemplateId,
            })
          : Promise.resolve(null),
      ]);
      return job;
    },
    /* Saving is not leaving: a posting is built over several passes. The new
       posting takes its own URL, so the next save updates it rather than
       creating a second one, and the step comes along. */
    onSuccess: async (job) => {
      toast.success("Job created");
      await queryClient.invalidateQueries({ queryKey: orpc.workforce.key() });
      await navigate({
        to: "/hiring/jobs/$jobId",
        params: { jobId: job.id },
        search: { step },
        replace: true,
      });
    },
    onError: (error: Error) => toast.error(errorWords(error)),
  });

  if (!canManage) {
    return (
      <>
        <PageHeader title="New job posting" />
        <EmptyState
          title="You cannot post jobs"
          description="Ask an administrator for hiring access."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="New job posting"
        meta="What the job is, what applicants are asked, and what they sign."
      />
      <JobBuilder
        mode="create"
        pending={save.isPending}
        initial={{
          values: emptyJobValues(facilityId === "all" ? "" : facilityId),
          screeningQuestions: [],
          screeningTemplateId: null,
          jobAppSections: [],
          jobAppTemplateId: null,
          formTemplateIds: [],
          documents: [],
        }}
        step={step}
        onStepChange={(next) => void navigate({ to: ".", search: { step: next }, replace: true })}
        onSubmit={(draft) => save.mutate(draft)}
      />
    </>
  );
}
